import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { SessionStore } from 'app/core/services/session.store';
import { useToaster } from 'app/shared/composables/use-toaster';
import { ExclusiveRequestCoordinator } from 'app/shared/services/exclusive-request-coordinator';

import { NotificationGateway } from '../gateway/notification-gateway';
import { NotificationId } from '../models/notification';
import { NotificationResource } from '../resource/notification-resource';

export function useMarkNotificationAsRead() {
    const gateway = inject(NotificationGateway);
    const notificationResource = inject(NotificationResource);
    const requestCoordinator = inject(ExclusiveRequestCoordinator);
    const user = inject(SessionStore).user;
    const translate = inject(TranslateService);
    const toaster = useToaster();

    const markAsRead = async (id: NotificationId): Promise<void> => {
        const requestUser = user();
        if (!requestUser) return;

        await requestCoordinator.run('notifications.markAsRead', [requestUser.id, id], async () => {
            const notification = notificationResource.findById(id);
            if (!notification || notification.read) return;

            if (!notificationResource.setRead(requestUser.id, id, true)) return;

            try {
                await gateway.markAsRead(id);
            } catch {
                if (user()?.id === requestUser.id) {
                    notificationResource.setRead(requestUser.id, id, false);
                    toaster.error({
                        title: translate.instant('NOTIFICATION_MARK_AS_READ_ERROR'),
                        duration: 5 * 1000,
                    });
                }
            } finally {
                if (user()?.id === requestUser.id) {
                    notificationResource.reload();
                }
            }
        });
    };

    return { markAsRead };
}
